import {
  DocumentBlobKind,
  DocumentId,
  DocumentKind,
  SubdomainName,
} from "@bitory-gmbh/bsm";
import {
  StoreRequest,
  useDocumentBlobStoreMutation,
} from "@/services/backend/filestorage/api/document-blob-api";
import { ChangeEvent, DragEvent, useEffect, useState } from "react";
import { toast } from "sonner";
import { Label } from "@/shared/components/ui/label";
import { UploadCloud } from "lucide-react";
import { cn } from "@/shared/lib/utils";
import t from "@/lang/lang";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { TextInput } from "@/shared/components/form/text-input";
import {
  inputValidationErrorMessages,
  InputValidationErrors,
} from "@/shared/components/errors/input-error-messages";
import { Button } from "@/shared/components/ui/button";
import { Spinner } from "@/shared/components/ui/spinner";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { DocumentBlobKindSelect } from "@/services/backend/filestorage/components/document-blob-kind-select";

export function DocumentBlobUploadArea({
  subdomain,
  documentKind,
  documentId,
  defaultKind = DocumentBlobKind.Attachment,
  accept,
  disabled = false,
}: {
  subdomain: SubdomainName;
  documentKind: DocumentKind;
  documentId: DocumentId;
  defaultKind?: DocumentBlobKind;
  accept?: string;
  disabled?: boolean;
}) {
  const [request, setRequest] = useState<StoreRequest>({
    subdomain,
    documentKind,
    documentId,
    kind: defaultKind,
    filename: "",
    data: new File([], ""),
    version: "",
    mediaType: "",
  });
  const [put, { isLoading, error, isSuccess, reset }] =
    useDocumentBlobStoreMutation();

  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (isSuccess) {
      toast.success("Datei erfolgreich hochgeladen");
      reset();
      setRequest({
        subdomain,
        documentKind,
        documentId,
        kind: defaultKind,
        filename: "",
        data: new File([], ""),
        version: "",
        mediaType: "",
      });
      setOpen(false);
    }
  }, [documentId, documentKind, isSuccess, defaultKind, reset, subdomain]);

  const doSetRequest = (e: ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || !e.target.files[0]) {
      return;
    }

    setRequest({
      ...request,
      filename: e.target.files[0].name,
      data: e.target.files[0],
      mediaType: e.target.files[0].type,
    });

    e.target.value = "";
    e.target.files = null;

    setOpen(true);
  };

  const doUpload = () => {
    if (isLoading) return;

    put(request);
  };

  return (
    <div>
      <Dialog
        open={open}
        onOpenChange={(state) => {
          setOpen(state);
          if (!state) {
            reset();
            setRequest({
              subdomain,
              documentKind,
              documentId,
              kind: defaultKind,
              filename: "",
              data: new File([], ""),
              version: "",
              mediaType: "",
            });
          }
        }}
      >
        <DialogContent className="sm:max-w-4xl">
          <DialogHeader>
            <DialogTitle>{t("Datei Upload")}</DialogTitle>
          </DialogHeader>
          <TextInput
            label={t("Dateiname")}
            field="filename"
            text={request.filename}
            onTextChange={(filename) => setRequest({ ...request, filename })}
            errors={inputValidationErrorMessages(error, "filename")}
          />
          <TextInput
            label={t("Version")}
            field="version"
            text={request.version}
            onTextChange={(version) => setRequest({ ...request, version })}
            errors={inputValidationErrorMessages(error, "version")}
            hint={t(
              "Optionale Angabe zur Version des Dokuments welcher die Datei zugeordnet werden soll.",
            )}
            // this is hidden until document versioning is properly introduced
            className="hidden"
          />
          <div className="grid items-start gap-1.5">
            <Label>{t("Art der Datei")}</Label>
            <DocumentBlobKindSelect
              availableDocumentBlobKinds={[
                DocumentBlobKind.Representation,
                DocumentBlobKind.Attachment,
              ]}
              value={request.kind}
              onValueChange={(k) =>
                setRequest({ ...request, kind: k as DocumentBlobKind })
              }
            />
            <InputValidationErrors error={error} field="kind" />
          </div>
          <RTKQueryErrorAlert error={error} ignoreValidationError={false} />
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">{t("Abbrechen")}</Button>
            </DialogClose>
            <Button onClick={doUpload} disabled={isLoading}>
              {isLoading && <Spinner />}
              <span>{t("Hochladen")}</span>
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Label
        className={cn(
          "border-muted-foreground/40 bg-muted hover:bg-muted/50 flex h-64 w-full cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed hover:border-solid",
          disabled && "pointer-events-none cursor-not-allowed opacity-50",
        )}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          if (disabled) {
            return;
          }
          e.preventDefault();
          const file = extractFile(e, accept);
          if (!file) {
            return;
          }
          setRequest((req) => ({
            ...req,
            filename: file.name,
            data: file,
            mediaType: file.type,
          }));
          setOpen(true);
        }}
      >
        <UploadCloud
          className={cn("h-12 w-12", isLoading ? "animate-bounce" : "")}
        />
        {isLoading ? (
          <span className="mt-3">{t("Lade hoch ...")}</span>
        ) : (
          <>
            <span className="mt-3">{t("Datei hochladen")}</span>
            <span className="text-muted-foreground mt-3 font-normal">
              {t("Klick oder Drag & Drop")}
            </span>
            <span className="text-muted-foreground pt-3 text-xs font-normal">
              {(accept || "")
                .split(",")
                .map((s) => s.substring(s.indexOf("/") + 1))
                .join(", ")}
            </span>
          </>
        )}
        <input
          type="file"
          hidden
          accept={accept}
          onChange={doSetRequest}
          disabled={isLoading || disabled}
        />
      </Label>
    </div>
  );
}

function extractFile(
  e: DragEvent<HTMLLabelElement>,
  accept?: string,
): File | undefined {
  if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
    const item = e.dataTransfer.items[0];
    // ignore none-file items and none-accepted media types
    if (item.kind !== "file" || (accept && !accept.includes(item.type))) {
      return undefined;
    }

    const file = item.getAsFile();
    if (!file) {
      return undefined;
    }

    return file;
  }

  return e.dataTransfer.files[0];
}
